'use client';
import { useEffect, useRef, useState } from 'react';

import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const roles = [
  'Full Stack Engineer',
  'E-Commerce Architect',
  'Web Designer & Developer',
  'Graphics & Video Creator',
  'Headless CMS & Database',
  'UI/UX Strategist & Engineer',
  'Mobile App Design & Dev',
  'DevOps & Cloud Engineer',
  'Project & Team Lead',
];

const floatingBadges = [
  { label: 'React', color: '#61DAFB', icon: 'CodeBracketIcon', delay: '0s' },
  { label: 'Node.js', color: '#68A063', icon: 'ServerIcon', delay: '1s' },
  { label: 'TypeScript', color: '#3178C6', icon: 'CommandLineIcon', delay: '2s' },
  { label: 'AWS', color: '#FF9900', icon: 'CloudIcon', delay: '0.5s' },
  { label: 'Flutter', color: '#02569B', icon: 'DevicePhoneMobileIcon', delay: '1.5s' },
  { label: 'Docker', color: '#2496ED', icon: 'CubeIcon', delay: '2.5s' },
];

export default function HeroSection() {
  const [roleIndex, setRoleIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const currentRole = roles[roleIndex];
    const typingSpeed = isDeleting ? 40 : 80;
    const pauseTime = isDeleting ? 0 : 1800;

    if (!isDeleting && charIndex === currentRole.length) {
      timeoutRef.current = setTimeout(() => setIsDeleting(true), pauseTime);
      return;
    }

    if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setRoleIndex((prev) => (prev + 1) % roles.length);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setDisplayText(
        isDeleting
          ? currentRole.substring(0, charIndex - 1)
          : currentRole.substring(0, charIndex + 1)
      );
      setCharIndex((prev) => (isDeleting ? prev - 1 : prev + 1));
    }, typingSpeed);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [charIndex, isDeleting, roleIndex]);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-10"
    >
      {/* Ambient glow orbs */}
      <div
        className="glow-orb"
        style={{
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(108,99,255,0.15) 0%, transparent 70%)',
          top: '-100px',
          left: '-200px',
        }}
      />

      <div
        className="glow-orb"
        style={{
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(0,212,255,0.1) 0%, transparent 70%)',
          top: '200px',
          right: '-100px',
          animationDelay: '3s',
        }}
      />

      <div
        className="glow-orb"
        style={{
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(16,217,160,0.08) 0%, transparent 70%)',
          bottom: '0px',
          left: '40%',
          animationDelay: '5s',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text Content */}
          <div className="space-y-8">
            {/* Availability badge */}
            <div className="initially-hidden" style={{ animationDelay: '0.1s' }}>
              <div className="availability-badge inline-flex">
                <div className="availability-dot" />
                <span>Available for New Projects</span>
              </div>
            </div>

            {/* Main heading */}

            <div className="initially-hidden" style={{ animationDelay: '0.2s' }}>
              <h1 className="hero-title text-5xl sm:text-7xl md:text-8xl text-[var(--text-primary)] leading-none flex items-center gap-x-3">
                <span>Akif</span>
                <span className="gradient-text">Khan</span>
              </h1>
            </div>

            {/* Typewriter role */}
            <div className="initially-hidden" style={{ animationDelay: '0.35s' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-px bg-gradient-to-r from-[#6C63FF] to-[#00D4FF]" />
                <div
                  className="text-xl md:text-2xl font-semibold"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <span>{displayText}</span>
                  <span className="typewriter-cursor" />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="initially-hidden" style={{ animationDelay: '0.5s' }}>
              <p
                className="text-lg leading-relaxed max-w-lg"
                style={{ color: 'var(--text-secondary)' }}
              >
                I craft world-class digital experiences — from high-performance websites and
                scalable web apps to UI/UX, mobile apps, eCommerce, and cloud/DevOps, optimized,
                secure, and ready. Every pixel. Every line of code. Every system. Done right.
              </p>
            </div>

            {/* Stats row */}
            <div className="initially-hidden" style={{ animationDelay: '0.6s' }}>
              <div className="flex gap-8">
                {[
                  { value: '10+', label: 'Years Experience' },
                  { value: '200+', label: 'Projects Shipped' },
                  { value: '50+', label: 'Happy Clients' },
                ].map((stat) => (
                  <div key={stat.label}>
                    <div
                      className="text-2xl font-extrabold gradient-text"
                      style={{ letterSpacing: '-0.03em' }}
                    >
                      {stat.value}
                    </div>
                    <div
                      className="text-xs font-medium mt-1"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div
              className="initially-hidden flex flex-wrap gap-4"
              style={{ animationDelay: '0.7s' }}
            >
              <a href="#contact">
                <button className="btn-primary relative z-10 flex items-center gap-2">
                  <Icon name="BriefcaseIcon" size={18} />
                  <span>Let’s Build</span>
                </button>
              </a>
              <a href="#portfolio">
                <button className="btn-ghost flex items-center gap-2">
                  <Icon name="EyeIcon" size={18} />
                  <span>View Work</span>
                </button>
              </a>

              {/*
              <a
                href="javascript:void(0)"
                download
                className="flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold"
                style={{
                  background: 'rgba(16,217,160,0.1)',
                  border: '1px solid rgba(16,217,160,0.3)',
                  color: '#10D9A0',
                  transition: 'all 0.3s ease',
                }}
              >
                <Icon name="ArrowDownTrayIcon" size={18} />
                <span>Download CV</span>
              </a>
             */}

              <a
                href="https://wa.me/923343374983?text=Hi%20Akif%2C%20I%20saw%20your%20portfolio%20and%20want%20to%20discuss%20a%20project!"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold"
                style={{
                  background: 'rgba(37,211,102,0.1)',
                  border: '1px solid rgba(37,211,102,0.3)',
                  color: '#25D366',
                  transition: 'all 0.3s ease',
                }}
                title="Chat on WhatsApp"
              >
                <Icon name="ChatBubbleLeftIcon" size={18} />
                <span>WhatsApp Me</span>
              </a>
            </div>

            {/* Social links */}
            <div className="initially-hidden flex gap-4" style={{ animationDelay: '0.8s' }}>
              {[
                { icon: 'CodeBracketIcon', href: '#', label: 'GitHub' },
                { icon: 'LinkIcon', href: '#', label: 'LinkedIn' },
                { icon: 'EnvelopeIcon', href: 'mailto:akif.tm@gmail.com', label: 'Email' },
                { icon: 'ChatBubbleLeftIcon', href: '#', label: 'Twitter/X' },
              ].map(({ icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  title={label}
                  className="w-10 h-10 rounded-xl flex items-center justify-center glass-card hover:scale-110 transition-all duration-300"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Icon name={icon as 'CodeBracketIcon'} size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Right: Profile Image + Floating Cards */}
          <div
            className="relative flex items-center justify-center initially-hidden"
            style={{ animationDelay: '0.3s', minHeight: '520px' }}
          >
            {/* Profile image */}
            <div className="relative z-10" style={{ animation: 'float-2 8s ease-in-out infinite' }}>
              <div
                className="relative rounded-[32px] overflow-hidden"
                style={{
                  width: '300px',
                  height: '360px',
                  boxShadow: '0 30px 80px rgba(108,99,255,0.3), 0 0 0 1px rgba(108,99,255,0.2)',
                }}
              >
                {/* Gradient border effect */}
                <div
                  className="absolute inset-0 rounded-[32px]"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(108,99,255,0.4), rgba(0,212,255,0.3))',
                    zIndex: 0,
                  }}
                />

                <AppImage
                  src="https://ik.imagekit.io/akif/aw.png?updatedAt=1773315122461"
                  alt="Akif Khan - Full Stack Developer and Designer professional headshot"
                  fill
                  className="object-cover relative z-10"
                />

                {/* Overlay gradient */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-24 z-20"
                  style={{
                    background: 'linear-gradient(to top, rgba(8,11,20,0.9), transparent)',
                  }}
                />

                {/* Name tag */}
                <div className="absolute bottom-4 left-4 right-4 z-30 glass rounded-xl p-3">
                  <div className="text-sm font-bold text-white">Akif Khan</div>
                  <div className="text-xs" style={{ color: '#00D4FF' }}>
                    Full Stack Engineer & Designer
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Card 1 — React */}
            <div
              className="absolute glass-card p-4 rounded-2xl z-20 lg:block"
              style={{
                top: '5%',
                left: '-5%',
                animation: 'float-1 6s ease-in-out infinite',
                minWidth: '160px',
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold"
                  style={{ background: 'rgba(97,218,251,0.15)', color: '#61DAFB' }}
                >
                  ⚛
                </div>
                <div>
                  <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                    React NextJS
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Tailwind &amp; Typescript
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Card 2 — Cloud */}
            <div
              className="absolute glass-card p-4 rounded-2xl z-20 lg:block"
              style={{
                top: '20%',
                right: '-5%',
                animation: 'float-3 7s ease-in-out infinite 1s',
                minWidth: '170px',
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(255,153,0,0.15)', color: '#FF9900' }}
                >
                  <Icon name="CloudIcon" size={20} />
                </div>
                <div>
                  <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                    VueJS / Laravel
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Enterprise WebApp
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Card 3 — Projects */}
            <div
              className="absolute glass-card p-4 rounded-2xl z-20 lg:block"
              style={{
                bottom: '0%',
                left: '0%',
                animation: 'float-4 9s ease-in-out infinite 0.5s',
                minWidth: '160px',
              }}
            >
              <div className="text-center">
                <div
                  className="text-2xl font-extrabold gradient-text"
                  style={{ letterSpacing: '-0.03em' }}
                >
                  200+
                </div>
                <div className="text-xs font-medium mt-1" style={{ color: 'var(--text-muted)' }}>
                  Projects Delivered
                </div>
                <div className="flex justify-center gap-1 mt-2">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: i < 4 ? '#6C63FF' : 'rgba(108,99,255,0.3)' }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Floating Card 4 — Live Project */}
            <div
              className="absolute glass-card p-3 rounded-2xl z-20 lg:block"
              style={{
                bottom: '0%',
                right: '-5%',
                animation: 'float-2 8s ease-in-out infinite 2s',
                minWidth: '180px',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    background: '#10D9A0',
                    boxShadow: '0 0 8px rgba(16,217,160,0.8)',
                    animation: 'pulse-availability 2s ease-in-out infinite',
                  }}
                />

                <span className="text-xs font-semibold" style={{ color: '#10D9A0' }}>
                  Currently Building
                </span>
              </div>
              <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                Enterprise SaaS Platform
              </div>
              <div
                className="mt-2 h-1.5 rounded-full"
                style={{ background: 'rgba(255,255,255,0.1)' }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: '72%',
                    background: 'linear-gradient(90deg, #6C63FF, #00D4FF)',
                  }}
                />
              </div>
              <div className="text-xs mt-1 text-right" style={{ color: 'var(--text-muted)' }}>
                72% complete
              </div>
            </div>

            {/* Background grid decoration */}
            <div
              className="absolute inset-0 opacity-5"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(108,99,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(108,99,255,0.5) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
            />
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className="flex justify-center mt-16 initially-hidden"
          style={{ animationDelay: '1.2s' }}
        >
          <a
            href="#about"
            className="flex flex-col items-center gap-2"
            style={{ color: 'var(--text-muted)' }}
          >
            <span className="text-xs font-mono">scroll down</span>
            <div
              className="w-6 h-10 rounded-full border flex items-start justify-center pt-2"
              style={{ borderColor: 'var(--glass-border)' }}
            >
              <div
                className="w-1.5 h-3 rounded-full"
                style={{
                  background: 'linear-gradient(to bottom, #6C63FF, #00D4FF)',
                  animation: 'float-1 2s ease-in-out infinite',
                }}
              />
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
